import React from 'react';
import { GraduationCap, Plus, RefreshCw, Send } from 'lucide-react';
import OrphanApprenticesPanel from '../OrphanApprenticesPanel';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin — Cohorts tab. Cards per Master license + grouped apprentices.
 * Extracted from AdminPanel.jsx on 29/05/2026 (code review refactor).
 */
export default function CohortsTab({
  cohorts, masterLicenses, orphanApprentices, bulkResendingId,
  token, onCreate, onBulkResend, onLicensesChanged,
}) {
  return (
    <div className="space-y-6" data-testid="cohorts-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-purple-400" /> Cohortes pédagogiques
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Vue regroupée par licence Responsable Pédagogique. Chaque cohorte voit en temps réel les jeux de ses apprenants via la page <code className="font-mono">/supervision</code>.
          </p>
        </div>
        <button onClick={onCreate} className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2.5 rounded-lg text-sm font-bold transition-colors" data-testid="cohort-create-btn">
          <Plus className="w-4 h-4" /> Nouvelle licence
        </button>
      </div>

      {masterLicenses.length === 0 && (
        <div className="bg-ink-800 border border-ink-600 rounded-xl p-8 text-center" data-testid="no-master-empty">
          <GraduationCap className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-sm text-slate-400">Aucune licence Responsable Pédagogique créée.</p>
          <p className="text-[11px] text-slate-500 mt-1">
            Crée une licence en sélectionnant &quot;Responsable Pédagogique&quot; dans le modal.
          </p>
        </div>
      )}

      {cohorts.map(({ master, children }) => (
        <CohortCard
          key={master.id} master={master} children={children}
          bulkResendingId={bulkResendingId} token={token}
          onBulkResend={onBulkResend} onLicensesChanged={onLicensesChanged}
        />
      ))}

      {orphanApprentices.length > 0 && (
        <OrphanApprenticesPanel
          orphans={orphanApprentices} masterLicenses={masterLicenses}
          token={token} onChanged={onLicensesChanged}
        />
      )}
    </div>
  );
}

function CohortCard({ master, children, bulkResendingId, token, onBulkResend, onLicensesChanged }) {
  return (
    <div className="bg-ink-800 border border-ink-600 rounded-xl overflow-hidden" data-testid={`cohort-${master.id}`}>
      <div className="bg-gradient-to-r from-purple-700/30 to-purple-900/30 px-5 py-4 border-b border-purple-500/30">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-md bg-purple-500/20 border border-purple-400/40 flex items-center justify-center flex-shrink-0">
              <GraduationCap className="w-5 h-5 text-purple-300" />
            </div>
            <div className="min-w-0">
              <p className="font-bold text-slate-100 truncate">{master.holder_name}</p>
              <p className="ph-no-capture text-[11px] font-mono text-purple-300/80">{master.license_key}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            {children.length > 0 && (
              <button
                onClick={() => onBulkResend(master, children)}
                disabled={bulkResendingId === master.id}
                className="text-[10px] px-2.5 py-1.5 rounded-md bg-purple-500/15 hover:bg-purple-500/25 border border-purple-400/40 text-purple-200 font-semibold uppercase tracking-wider transition-colors disabled:opacity-50 flex items-center gap-1.5"
                title="Renvoyer le welcome email à tous les apprenants de cette cohorte"
                data-testid={`bulk-resend-cohort-${master.id}`}
              >
                {bulkResendingId === master.id ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                Renvoyer welcome ({children.length})
              </button>
            )}
            <div className="text-right">
              <p className="text-2xl font-black font-mono text-purple-300">{children.length}</p>
              <p className="text-[10px] uppercase tracking-wider text-purple-400/70 font-mono">apprenant·s</p>
            </div>
          </div>
        </div>
      </div>
      {children.length === 0 ? (
        <div className="px-5 py-6 text-center text-xs text-slate-500" data-testid={`cohort-empty-${master.id}`}>
          Aucun apprenant rattaché. Utilise la colonne « Type / Cohorte » dans l&apos;onglet Licences pour les rattacher.
        </div>
      ) : (
        <CohortTable master={master} children={children} token={token} onLicensesChanged={onLicensesChanged} />
      )}
    </div>
  );
}

function CohortTable({ master, children, token, onLicensesChanged }) {
  const detach = async (child) => {
    if (!window.confirm(`Détacher ${child.holder_name} de la cohorte ${master.holder_name} ?`)) return;
    await fetch(`${API}/api/licenses/${child.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ master_license_id: null }),
    });
    onLicensesChanged();
  };
  return (
    <table className="w-full text-sm">
      <thead className="bg-ink-700/30 border-b border-ink-600">
        <tr>
          <th className="text-left px-4 py-2 text-[10px] font-semibold text-slate-500 uppercase">Apprenant</th>
          <th className="text-left px-4 py-2 text-[10px] font-semibold text-slate-500 uppercase">Clé</th>
          <th className="text-left px-4 py-2 text-[10px] font-semibold text-slate-500 uppercase">Statut</th>
          <th className="text-left px-4 py-2 text-[10px] font-semibold text-slate-500 uppercase">Activations</th>
          <th className="text-right px-4 py-2 text-[10px] font-semibold text-slate-500 uppercase">Détacher</th>
        </tr>
      </thead>
      <tbody>
        {children.map((child) => {
          const expired = child.expires_at && new Date(child.expires_at) < new Date();
          return (
            <tr key={child.id} className="border-b border-ink-600 hover:bg-ink-700/40" data-testid={`cohort-child-${child.id}`}>
              <td className="px-4 py-2 text-slate-200">{child.holder_name}</td>
              <td className="px-4 py-2"><code className="ph-no-capture text-[11px] font-mono bg-ink-700/40 px-2 py-0.5 rounded">{child.license_key}</code></td>
              <td className="px-4 py-2">
                {!child.is_active ? (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-ink-700/40 text-slate-400">Désactivée</span>
                ) : expired ? (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-danger/10 text-danger">Expirée</span>
                ) : (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-money/10 text-money">Active</span>
                )}
              </td>
              <td className="px-4 py-2 text-xs text-slate-400 font-medium">{child.activations || 0}</td>
              <td className="px-4 py-2 text-right">
                <button
                  onClick={() => detach(child)}
                  className="text-[10px] px-2 py-1 rounded bg-warn/10 text-warn hover:bg-warn/20 transition-colors"
                  data-testid={`detach-${child.id}`}
                >
                  Détacher
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
